import { Injectable } from '@angular/core';
import { Observable, distinctUntilChanged, filter, fromEvent, map, merge, shareReplay, startWith } from 'rxjs';

/**
 * Browser connectivity as an observable.
 *
 * Before this, `navigator.onLine` was read in exactly one place (CommonService) and no
 * online/offline listener existed anywhere, so a user who dropped connection mid-load
 * had to reload the page by hand once signal came back.
 */
@Injectable({ providedIn: 'root' })
export class NetworkStatusService {

  /** Current connectivity, replayed to late subscribers. */
  readonly isOnline$: Observable<boolean>;

  /**
   * Fires only on the offline -> online edge.
   *
   * Intentionally exposed rather than auto-wired: ONLY list and read pages should
   * subscribe. No write flow, signing flow or payment flow may refetch on reconnect —
   * a duplicated loan bid or fee payment is far worse for a user than pressing refresh.
   */
  readonly reconnected$: Observable<void>;

  constructor() {
    const online$ = fromEvent(window, 'online').pipe(map(() => true));
    const offline$ = fromEvent(window, 'offline').pipe(map(() => false));

    this.isOnline$ = merge(online$, offline$).pipe(
      startWith(this.currentlyOnline()),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.reconnected$ = this.isOnline$.pipe(
      // skip the startWith seed so a page opened while online does not fire immediately
      filter((isOnline, index) => isOnline && index > 0),
      map(() => undefined)
    );
  }

  get isOnline(): boolean {
    return this.currentlyOnline();
  }

  /** `navigator.onLine` is absent in non-browser test contexts; assume online there. */
  private currentlyOnline(): boolean {
    return typeof navigator === 'undefined' || navigator.onLine !== false;
  }
}
